export const uiMessages = {
  'auth.common.authSessionLoading': 'Auth session is still loading. Try again',
  'auth.common.passwordRequired': 'Password is required',
  'auth.common.reverificationAdditionalRequired':
    'Additional verification is required. Use another sign in method',
  'auth.common.reverificationFailed': 'Could not verify your session. Try again',
  'auth.common.reverificationCancelled': 'Verification cancelled',
  'auth.common.signOutAndRetry': 'Sign out and retry',
  'auth.common.sessionExpiredSignIn': 'Your session expired. Sign in again',

  'auth.signIn.frameErrorTitle': 'Could not finish sign in. Try again',
  'auth.signIn.accountNotFound': 'Could not find your account',
  'auth.signIn.unsupportedSecondFactorMethod':
    'A second-factor method is required, but no supported method was returned',
  'auth.signIn.unableToSendVerificationCode': 'Unable to send a verification code. Try again',
  'auth.signIn.unsupportedStatus': ({ status }: { status: string }) =>
    `Sign in requires an unsupported step (${status})`,
  'auth.signIn.verificationCodeSent': 'Verification code sent. Check your inbox',
  'auth.signIn.verificationCodeRequired': 'Verification code is required',
  'auth.signIn.noSecondFactorSelected': 'No second-factor method is selected',
  'auth.signIn.additionalUnsupportedStatus': ({ status }: { status: string }) =>
    `Sign in requires an additional unsupported step (${status})`,
  'auth.signIn.staleSessionRecoveryFailed':
    'Unable to recover from a stale session. Try signing in again',
  'auth.signIn.unableToSignIn': 'Unable to sign in. Try again',
  'auth.signIn.emailRequired': 'Email is required',
  'auth.signIn.restoreFlowPrompt': 'Restore your deleted account with the email code flow',
  'auth.signIn.resetVerificationCodeSent': 'Verification code sent. Check your inbox',
  'auth.signIn.passwordUpdatedSignIn':
    'Password updated. Sign in with your new password to continue',
  'auth.signIn.restoreCodeRefreshed':
    'Your restore code expired. A new code has been sent to your email',
  'auth.signIn.newPasswordRequired': 'New password is required',
  'auth.signIn.passwordsDoNotMatch': 'Passwords do not match',
  'auth.signIn.resetAdditionalSteps':
    'Password reset requires additional steps that are not configured yet',

  'auth.signUp.frameErrorTitle': 'Could not finish sign up. Try again',
  'auth.signUp.mustAcceptPolicies':
    'You must accept the Terms of Service and Acceptable Use Policy before signing up',
  'auth.signUp.staleSessionSignedOut': 'We signed you out of a stale session. Try again',
  'auth.signUp.unableToSignUp': 'Unable to sign up. Try again',
  'auth.signUp.deletedAccountRestore':
    'This account was deleted. Restore your account or contact support',
  'auth.signUp.deletedAccountExpired':
    'This account was deleted and cannot be restored. Contact support',
  'auth.signUp.emailVerifiedFinalizingSession': 'Email verified. Finalizing your session',
  'auth.signUp.couldNotEstablishSession': 'Could not establish your session. Sign in',
  'auth.signUp.verificationDidNotComplete': 'Verification did not complete. Try again',
  'auth.signUp.verificationCodeSent': 'Verification code sent. Check your inbox',

  'account.profile.updateCta': 'Update Profile',
  'account.profile.emailLabel': 'Email',
  'account.profile.emailManagedByProvider': 'Email is managed by your sign in provider',
  'account.profile.resendVerificationCta': 'Resend Verification Email',
  'account.profile.emailAlreadyInUse': 'Email already in use',
  'account.profile.invalidEmail': 'Enter a valid email address',
  'account.profile.authSessionLoading': 'Auth session is still loading. Try again',
  'account.profile.verificationCodeSent': 'Verification code sent. Check your inbox',
  'account.profile.updateCancelled': 'Email update cancelled',
  'account.profile.startVerificationFailed': 'Unable to start email verification. Try again',
  'account.profile.updateFailed': 'Unable to update profile. Try again',
  'account.profile.verificationCodeRequired': 'Verification code is required',
  'account.profile.emailUpdated': 'Email updated',
  'account.profile.profileUpdated': 'Profile updated',
  'account.profile.verificationDidNotComplete': 'Verification did not complete. Try again',
  'account.profile.updatePrimaryFailed': 'Unable to update your primary email',
  'account.profile.verificationFailed': 'Verification failed. Try again',
  'account.profile.resendVerificationFailed': 'Unable to resend verification code',
  'account.profile.emailAlreadyVerified': 'Email is already verified',
  'account.profile.verificationEmailSent': 'Verification email sent',
  'account.profile.removePreviousEmailFailed':
    'Primary email updated, but previous email could not be removed',
  'account.profile.verifyEmailTitle': 'Verify Email',
  'account.profile.verifyEmailDescription': ({ email }: { email: string }) =>
    `Enter the verification code sent to ${email}`,
  'account.profile.verifyEmailCodeLabel': 'Verification Code',
  'account.profile.verifyEmailCodePlaceholder': 'Enter verification code',
  'account.profile.verifyEmailSubmit': 'Verify',
  'account.profile.didNotReceiveCode': "Didn't receive a code?",
  'account.profile.resendIn': ({ seconds }: { seconds: number }) => `Resend in ${seconds}s`,
  'account.profile.resend': 'Resend',
  'account.profile.reverifySessionTitle': 'Verify Your Session',
  'account.profile.reverifySessionDescription':
    'Enter your current password to continue updating your email',
  'account.profile.reverifyContinue': 'Continue',

  'account.password.changeCta': 'Change Password',
  'account.password.unableToChange': 'Unable to change password right now',
  'account.password.changeCancelled': 'Password change cancelled',
  'account.password.passwordMismatch': 'New passwords do not match',
  'account.password.currentRequired': 'Current password is required',
  'account.password.mustDiffer': 'New password must be different from current password',
  'account.password.updated': 'Password updated',
  'account.password.reverifySessionTitle': 'Verify Your Session',
  'account.password.reverifySessionDescription':
    'Enter your current password to continue changing your password',
  'account.password.reverifyContinue': 'Continue',

  'account.delete.deleted': 'Account deleted',
  'account.delete.incorrectPassword': 'Incorrect password',
  'account.delete.failed': 'Unable to delete account. Try again',
  'account.delete.dialogTitle': 'Delete Account',
  'account.delete.dialogDescription':
    'Enter your password to confirm account deletion. This action cannot be undone',
  'account.delete.passwordLabel': 'Password',
  'account.delete.confirmLabel': 'Delete Account',
  'account.delete.panelTitle': 'Account Deletion',
  'account.delete.panelDescription': 'Permanently delete your account',
  'account.delete.panelCta': 'Delete Account',
} as const;

export type UiMessageCatalog = typeof uiMessages;
export type UiMessageKey = keyof UiMessageCatalog;

type AnyMessageFn = (args: unknown) => string;
type MessageFn = (args: Record<string, number | string>) => string;

export type UiMessageArgs<K extends UiMessageKey> = UiMessageCatalog[K] extends (
  ...args: infer TArgs
) => string
  ? TArgs[0]
  : undefined;

export const getMessage = <K extends UiMessageKey>(key: K, args?: UiMessageArgs<K>): string => {
  const entry = uiMessages[key];
  if (typeof entry === 'function') {
    return (entry as AnyMessageFn)(args);
  }
  return entry;
};

type MessageSampleArgs = {
  [K in UiMessageKey]?: UiMessageArgs<K>;
};

const sampleArgs: MessageSampleArgs = {
  'auth.signIn.unsupportedStatus': { status: 'needs_more_steps' },
  'auth.signIn.additionalUnsupportedStatus': { status: 'needs_more_steps' },
  'account.profile.verifyEmailDescription': { email: 'jane@example.com' },
  'account.profile.resendIn': { seconds: 30 },
};

export const getMessageSamples = (): Array<{ key: UiMessageKey; value: string }> => {
  return (Object.keys(uiMessages) as UiMessageKey[]).map((key) => {
    const entry = uiMessages[key];
    if (typeof entry === 'function') {
      const args = sampleArgs[key] as Record<string, number | string> | undefined;
      return { key, value: (entry as MessageFn)(args ?? {}) };
    }
    return { key, value: entry };
  });
};
